import React from "react";
import PropTypes from "prop-types";
import RightSheet from "../../../components/ui-components/RightSheet";
import FormButton from "../../../components/form-components/FormButton";
import TextArea from "../../../components/form-components/TextArea";

// Copy that differs between the two comment-required decisions this drawer supports.
// Reject is terminal; Return sends the request back to the preparer for revision.
const ACTION_COPY = {
  reject: {
    drawerTitle: "Reject approval request",
    fieldTitle: "Reason for rejection (required)",
    placeholder: "Explain why this request is being rejected...",
    submitLabel: "Reject",
    submittingLabel: "Rejecting...",
    fieldName: "reject-comment",
  },
  return: {
    drawerTitle: "Return approval request",
    fieldTitle: "What needs to change (required)",
    placeholder: "Explain what needs to change before this can be resubmitted...",
    submitLabel: "Return",
    submittingLabel: "Returning...",
    fieldName: "return-comment",
  },
};

/**
 * Drawer for rejecting (or returning-to-preparer) an approval request with a required
 * comment. Both decisions share the same required-comment prompt pattern; `action`
 * switches only the copy.
 */
const RejectDrawer = ({ open, onClose, onSubmit, isSubmitting, action = "reject" }) => {
  const [comment, setComment] = React.useState("");
  const copy = ACTION_COPY[action] || ACTION_COPY.reject;

  const handleSubmit = async () => {
    if (!comment.trim()) return;
    await onSubmit(comment.trim());
    setComment("");
  };

  const handleClose = () => {
    setComment("");
    onClose();
  };

  const footer = (
    <div className="flex gap-2 justify-end">
      <FormButton title="Cancel" style="tertiary" onClick={handleClose} disabled={isSubmitting} />
      <FormButton
        title={isSubmitting ? copy.submittingLabel : copy.submitLabel}
        style="primary"
        onClick={handleSubmit}
        disabled={!comment.trim() || isSubmitting}
      />
    </div>
  );

  return (
    <RightSheet
      open={open}
      onClose={handleClose}
      title={copy.drawerTitle}
      subtitle="Comment is required."
      footer={footer}
    >
      <div className="space-y-4">
        <TextArea
          isLoaded
          name={copy.fieldName}
          title={copy.fieldTitle}
          placeholder={copy.placeholder}
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          rows={4}
        />
      </div>
    </RightSheet>
  );
};

RejectDrawer.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  isSubmitting: PropTypes.bool,
  action: PropTypes.oneOf(["reject", "return"]),
};

export default RejectDrawer;
